<template>
  <el-dialog
    id="edit-modal"
    class="form-modal"
    :visible.sync="editVisible"
    v-loading="editProcessing"
    :fullscreen="true"
    :show-close="false"
    :modal="false"
  >
    <div id="edit-modal-header" class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">View / Edit Device</h1>
        <div class="modal-actions col-2">
          <el-button type="primary" @click="editOperation">Save</el-button>
          <el-button
            type="default"
            class="hidden-xs-only"
            @click="cancelOperation"
            >Cancel</el-button
          >
          <el-button
            type="default"
            icon="el-icon-close"
            class="hidden-sm-and-up"
            @click="cancelOperation"
          ></el-button>
        </div>
      </div>
    </div>

    <el-form>
      <recent-audits
			service="Device" :record="selectedDevice" />
      <el-tabs v-model="activeTabName">
        <el-tab-pane label="General" name="general">
          <div class="inline-form-group">
            <el-col :span="12">
              <el-input v-model="editForm.name" placeholder="Enter name">
                <template slot="prefix">Name</template>
              </el-input>
            </el-col>
            <el-col :span="1" />
            <el-col :span="12">
              <!-- Mac Input Field with Mask -->
              <div class="el-input el-input--prefix is-disabled">
                <input
                  type="text"
                  autocomplete="off"
                  placeholder="Enter MAC address"
                  class="el-input__inner"
                  v-model="editForm.mac"
                  v-mask="'NN:NN:NN:NN:NN:NN'"
                  disabled
                />
                <span class="el-input__prefix">MAC address</span>
              </div>
            </el-col>
          </div>

          <el-select
            v-model="editForm.phPmId"
            placeholder="Select phone model"
            disabled
          >
            <template slot="prefix">Phone model</template>
            <el-option
              v-for="(item, index) in deviceModelOptions"
              :key="index"
              :label="item.name"
              :value="item.id"
            />
          </el-select>

          <div v-if="phPmId">
            <h2 class="form-section-header">Assign extensions</h2>
            <div>
              <div v-for="(item, i) in phoneLines" :key="i">
                <div class="inline-form-group">
                  <el-col :span="8">
                    <dads-select
                      v-model="item.value"
                      :placeholder="`Select line ${i + 1}`"
                      remote
                      filterable
                      clearable
                      :remote-method="query => remoteSearchPhoneLines(query, i)"
                    >
                      <el-option
                        v-for="(item, index) in item.options"
                        :key="index"
                        :label="item.name + ' - ' + item.number"
                        :value="item.id"
                      />
                      <template slot="prefix">{{ `Line ${i + 1}` }}</template>
                    </dads-select>
                  </el-col>
                  <el-col :span="1" />
                  <el-col :span="10" v-if="'configVar' in item">
                    <el-select
                      v-model="item.configVar"
                      placeholder="Select line key"
                    >
                      <el-option
                        v-for="(n, i) in 12"
                        :key="i"
                        :value="n"
                        :label="n"
                      />
                      <template slot="prefix">Line key</template>
                    </el-select>
                  </el-col>
                  <el-col :span="1" />
                  <el-col
                    :span="2"
                    v-if="i === phoneLines.length - 1 && i !== 0"
                  >
                    <el-tooltip
                      class="item"
                      effect="dark"
                      :content="`Remove Line ${i}`"
                      placement="top"
                    >
                      <el-button
                        @click="removePhoneLine(i)"
                        icon="el-icon-delete"
                        class="row-button"
                      />
                    </el-tooltip>
                  </el-col>
                </div>
              </div>
              <div class="inline-form-group">
                <el-button
                  :disabled="phoneLines.length - 1 === numOfPhoneLinesAllowed"
                  @click="addPhoneLine"
                >
                  Add Line
                </el-button>
              </div>
            </div>
          </div>

          <div class="mt-20">
            <h2 class="form-section-header">Assign phonebooks</h2>
            <dads-select
              v-model="editForm.phonebooks"
              multiple
              clearable
              filterable
              placeholder="Phonebooks"
            >
              <template slot="prefix"
                ><span style="opacity: 0;">Phonebooks</span></template
              >
              <el-option
                v-for="(item, i) in phonebooksOptions"
                :key="i"
                :label="item.name"
                :value="item.id"
              />
            </dads-select>
          </div>

          <h2 class="form-section-header">Assign phone button layouts</h2>
          <dads-select
            v-model="editForm.buttonlayouts"
            multiple
            clearable
            filterable
            placeholder="Select button layouts"
          >
            <template slot="prefix">
              <span style="opacity: 0;">
                Button layouts
              </span>
            </template>
            <el-option
              v-for="(item, i) in buttonLayoutOptions"
              :key="i"
              :label="item.name"
              :value="item.id"
            />
          </dads-select>
          <el-tag class="mb-1" type="warning">
            <i class="el-icon-info" style="margin-right: 4px;" />
            Overrides key settings config tab
          </el-tag>

          <h2 class="form-section-header">Assign sidecar button layouts</h2>
          <dads-select
            v-model="editForm.sidecarButtonLayouts"
            multiple
            clearable
            filterable
            placeholder="Select button layouts"
          >
            <template slot="prefix">
              <span style="opacity: 0;">Sidecar button layouts</span>
            </template>
            <el-option
              v-for="(item, i) in buttonLayoutOptions"
              :key="i"
              :label="item.name"
              :value="item.id"
            />
          </dads-select>
          <el-tag class="mb-1">
            <i class="el-icon-info" style="margin-right: 4px;" />
            Yealink devices only
          </el-tag>
        </el-tab-pane>

        <!-- Settings Tab -->
        <el-tab-pane
          label="Settings"
          name="settings"
          v-if="!!provisionFields.length"
        >
          <div
            class="inline-form-group"
            v-for="(provision, i) in provisionFields"
            :key="i"
          >
            <el-col class="black-text" :span="8"
              >{{ provision.fieldName }}:
            </el-col>
            <el-col :span="1"></el-col>
            <el-col :span="16">
              <div v-if="provision.fieldType === 'Input'">
                <el-input
                  v-model="provision.value"
                  :placeholder="provision.fieldName"
                >
                </el-input>
              </div>

              <div v-if="provision.fieldType === 'Select'">
                <el-select
                  v-model="provision.value"
                  :placeholder="provision.fieldName"
                >
                  <el-option
                    v-for="(item, i) in provision.selectOptions[0]"
                    :key="i"
                    :label="item.label"
                    :value="item.value ? item.value : item.label"
                  >
                  </el-option>
                </el-select>
              </div>
            </el-col>
          </div>
        </el-tab-pane>

        <!-- KEY SETTINGS -->
        <el-tab-pane v-if="keySettings" name="keySettings" label="Key Settings">
          <div
            class="group-container"
            v-for="(groupIdx, i) in keySettingGroups"
            :key="i"
          >
            <h2 class="form-section-header">Key group {{ groupIdx }}</h2>
            <div
              class="inline-form-group"
              v-for="(provision, provisionIdx) in keySettings[
                `group${groupIdx}`
              ]"
              :key="provisionIdx"
            >
              <el-col class="black-text" :span="6"
                >{{ provision.fieldName }}:</el-col
              >
              <el-col :span="1" />
              <el-col :span="12">
                <!-- Key Type Fields -->
                <div
                  v-if="
                    provision.fieldType === 'Select' &&
                      /Key\s\d{1,2}\sType/.test(provision.fieldName)
                  "
                >
                  <el-select
                    v-model="provision.value"
                    @change="
                      onKeyTypeSelection(
                        groupIdx,
                        provisionIdx,
                        provision.fieldName,
                        provision,
                        $event
                      )
                    "
                    :placeholder="`Select ${provision.fieldName}`"
                  >
                    <el-option
                      v-for="(item, i) in provision.selectOptions[0]"
                      :key="i"
                      :label="item.label"
                      :value="item.value"
                    />
                  </el-select>
                </div>

                <!-- Key Name Fields -->
                <div
                  v-if="
                    provision.fieldType === 'SelectWithCreate' &&
                      /Key\s\d{1,2}\sName/.test(provision.fieldName)
                  "
                >
                  <div
                    v-if="
                      keyTypes[`${getKeyPrefix(provision.fieldName)}Type`] ===
                        '10' ||
                        keyTypes[`${getKeyPrefix(provision.fieldName)}Type`] ===
                          '13' || keyTypes[`${getKeyPrefix(provision.fieldName)}Type`] === '0' || keyTypes[`${getKeyPrefix(provision.fieldName)}Type`] === '19'
                    "
                  >
                    <el-input
                      v-model="provision.value"
                      :placeholder="provision.fieldName"
                    ></el-input>
                  </div>
                  <div
                    v-else-if="
                      keyTypes[`${getKeyPrefix(provision.fieldName)}Type`] ===
                        '16' || fieldIsAllowed(groupIdx) || keyTypes[`${getKeyPrefix(provision.fieldName)}Type`] ===
                        '15' || keyTypes[`${getKeyPrefix(provision.fieldName)}Type`] === '11'
                    "
                  >
                    <dads-select
                      filterable
                      remote
                      allow-create
                      :remote-method="
                        query =>
                          remoteSearchExtensionsName(query, provision.fieldName)
                      "
                      :placeholder="`Select ${provision.fieldName}`"
                      v-model="provision.value"
                    >
                      <el-option
                        v-for="(item, i) in keyNameOpts[
                          `${getKeyPrefix(provision.fieldName)}NameOpts`
                        ]"
                        :key="i"
                        :label="item.name"
                        :value="item.name"
                      >
                        <span style="float: left">{{ item.name }}</span>
                        <span
                          style="float: right; color: #8492a6; font-size: 13px"
                          >{{ item.number }}</span
                        >
                      </el-option>
                    </dads-select>
                  </div>
                  <el-input
                    v-else
                    disabled
                    v-model="provision.value"
                    :placeholder="provision.fieldName"
                  ></el-input>
                </div>

                <!-- Key Number Fields -->
                <div
                  v-if="
                    provision.fieldType === 'SelectWithCreate' &&
                      /Key\s\d{1,2}\sNumber/.test(provision.fieldName)
                  "
                >
                  <div
                    v-if="
                      keyTypes[`${getKeyPrefix(provision.fieldName)}Type`] ===
                        '13' || keyTypes[`${getKeyPrefix(provision.fieldName)}Type`] === '10'
                    "
                  >
                    <!-- Input Mask for Phone Number -->
                    <el-input
                      v-model="provision.value"
                      :placeholder="provision.fieldName"
                    >
                      <template slot="prefix">
                        {{ provision.fieldName }}
                      </template>
                    </el-input>
                  </div>
                  <div
                    v-else-if="
                      keyTypes[`${getKeyPrefix(provision.fieldName)}Type`] ===
                        '16' || fieldIsAllowed(groupIdx) || keyTypes[`${getKeyPrefix(provision.fieldName)}Type`] ===
                        '15' || keyTypes[`${getKeyPrefix(provision.fieldName)}Type`] === '11'
                    "
                  >
                    <dads-select
                      filterable
                      remote
                      allow-create
                      :remote-method="
                        query =>
                          remoteSearchExtensionsNumber(
                            query,
                            provision.fieldName
                          )
                      "
                      :placeholder="`Select ${provision.fieldName}`"
                      v-model="provision.value"
                    >
                      <el-option
                        v-for="(item, i) in keyNumberOpts[
                          `${getKeyPrefix(provision.fieldName)}NumberOpts`
                        ]"
                        :key="i"
                        :label="item.number"
                        :value="item.number"
                      >
                        <span style="float: left">{{ item.number }}</span>
                        <span
                          style="float: right; color: #8492a6; font-size: 13px"
                          >{{ item.name }}</span
                        >
                      </el-option>
                    </dads-select>
                  </div>
                  <div
                    v-else-if="
                      keyTypes[`${getKeyPrefix(provision.fieldName)}Type`] ===
                        '10' || keyTypes[`${getKeyPrefix(provision.fieldName)}Type`] === '19'
                    "
                  >
                    <dads-select
                      filterable
                      :placeholder="`Select ${provision.fieldName}`"
                      v-model="provision.value"
                    >
                      <el-option
                        v-for="(item, i) in keyNumberOpts[
                          `${getKeyPrefix(provision.fieldName)}NumberOpts`
                        ]"
                        :key="i"
                        :label="item.label"
                        :value="item.value"
                      />
                    </dads-select>
                  </div>
                  <el-input
                    v-else
                    disabled
                    v-model="provision.value"
                    :placeholder="provision.fieldName"
                  />
                </div>
              </el-col>
            </div>
            <el-tooltip
              v-if="i === keySettingGroups.length - 1"
              class="group-delete-btn"
              effect="dark"
              :content="`Remove Group ${groupIdx}`"
              placement="top"
            >
              <el-button
                @click="removeKeySettingGroup(i)"
                icon="el-icon-delete"
              />
            </el-tooltip>
          </div>
          <div style="margin-top: 20px;" class="inline-form-group">
            <el-button
              :disabled="keySettingGroups.length === 26"
              @click="addKeySettingGroup"
            >
              Add key
            </el-button>
          </div>
        </el-tab-pane>
      </el-tabs>
    </el-form>
  </el-dialog>
</template>

<script>
import feathersClient from "../../../../feathers/feathers-client";
import DadsSelect from "@/components/select";
import RecentAudits from "@/components/RecentAudits";

export default {
  name: "EditDeviceForm",
  props: {
    clearFormFields: {
      type: Boolean,
      required: true
    },
    editProcessing: { required: true },
    editVisible: { required: true },
    deviceModelOptions: { required: true },
    phonebooksOptions: { required: true },
    selectedDevice: { required: true },
    buttonLayoutOptions: { required: true }
  },
  components: { DadsSelect, RecentAudits },
  data() {
    return {
      activeTabName: "general",
      numOfPhoneLinesAllowed: 25,
      keySettings: null,
      keySettingGroups: [],

      keyTypes: {},

      keyNameOpts: {
        Key2NameOpts: [],
        Key3NameOpts: [],
        Key4NameOpts: [],
        Key5NameOpts: [],
        Key6NameOpts: [],
        Key7NameOpts: [],
        Key8NameOpts: [],
        Key9NameOpts: [],
        Key10NameOpts: [],
        Key11NameOpts: [],
        Key12NameOpts: [],
        Key13NameOpts: [],
        Key14NameOpts: [],
        Key15NameOpts: [],
        Key16NameOpts: [],
        Key17NameOpts: [],
        Key18NameOpts: [],
        Key19NameOpts: [],
        Key20NameOpts: [],
        Key21NameOpts: [],
        Key22NameOpts: [],
        Key23NameOpts: [],
        Key24NameOpts: [],
        Key25NameOpts: [],
        Key26NameOpts: [],
        Key27NameOpts: [],
      },

      keyNumberOpts: {
        Key2NumberOpts: [],
        Key3NumberOpts: [],
        Key4NumberOpts: [],
        Key5NumberOpts: [],
        Key6NumberOpts: [],
        Key7NumberOpts: [],
        Key8NumberOpts: [],
        Key9NumberOpts: [],
        Key10NumberOpts: [],
        Key11NumberOpts: [],
        Key12NumberOpts: [],
        Key13NumberOpts: [],
        Key14NumberOpts: [],
        Key15NumberOpts: [],
        Key16NumberOpts: [],
        Key17NumberOpts: [],
        Key18NumberOpts: [],
        Key19NumberOpts: [],
        Key20NumberOpts: [],
        Key21NameOpts: [],
        Key22NameOpts: [],
        Key23NameOpts: [],
        Key24NameOpts: [],
        Key25NameOpts: [],
        Key26NameOpts: [],
        Key27NameOpts: [],
      },

      phoneLines: [],

      editForm: {
        tenantId: null,
        name: "",
        mac: "",
        phPmId: null,
        phoneLines: [],
        phonebooks: [],
        settings: [],
        buttonlayouts: [],
        sidecarButtonLayouts: []
      },

      provisionFields: [],

      parkingLotName: "",
      parkingLotOptions: []
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },

    phPmId() {
      return this.editForm.phPmId;
    },
  },

  watch: {
    selectedTenant(val) {
      if (val) {
        this.getParkingLotOptions();
      }
    },

    clearFormFields(value) {
      if (value) {
        this.clearFields();
      }
    },

    phPmId(val) {
      if (val) {
        feathersClient
          .service("device-models")
          .get(val, { query: { tenantId: this.selectedTenant.id } })
          .then(({ settings, vpkGroups }) => {
            this.provisionFields = [...settings];

            if (Object.keys(vpkGroups).length > 0) {
              this.keySettings = { ...vpkGroups };
            } else {
              this.keySettings = null;
            }
          });
      }
    },

    async provisionFields(val) {
      if (val.length) {
        const { settings } = this.selectedDevice;

        if (settings.length) {
          settings.forEach(s => {
            this.provisionFields.forEach((p, idx) => {
              if (p.labelType === s.type && p.labelId === s.alId) {
                p.value = s.avValue;
              }
            });
          });

          let maxGroupIdx = 2;

          if (this.keySettings) {

            for (let i = 0; i < settings.length; i++) {
              const s = settings[i];

              for (let groupNum = 2; groupNum <= 27; groupNum++) {
                const group = this.keySettings[`group${groupNum}`];

                if(!group) continue;

                for (let idx = 0; idx < group.length; idx++) {
                  const p = group[idx];

                  if (p.labelType === s.type && p.labelId === s.alId) {
                    maxGroupIdx = groupNum;

                    p.value = s.avValue;
                    if (/Key\s\d{1,2}\sType/.test(p.fieldName)) {
                      const keyType = s.avValue;

                      this.keyTypes[p.fieldName.replace(/\s/g, "")] = keyType;

                      const keyPrefix = this.getKeyPrefix(p.fieldName);
                      if (keyType === "10" || keyType === "9") {
                        // If keyType is "Park"
                        this.keyNumberOpts[`${keyPrefix}NumberOpts`] = [
                          ...this.parkingLotOptions
                        ];
                      } else if (keyType === "16" || keyType === "1") {
                        // If keyType is "BLF"

                        const keyNumberQuery = this.provisionFields[idx + 1] ? this.provisionFields[idx + 1]
                          .value : null
                        const keyNameQuery = this.provisionFields[idx + 1] ? this.provisionFields[idx + 2]
                          .value: null;

                        if (keyNumberQuery) {
                          const sortby = "number";
                          const numberOpts = await this.searchExtensions(
                            keyNumberQuery,
                            sortby
                          );
                          this.keyNumberOpts[`${keyPrefix}NumberOpts`] = [
                            ...numberOpts
                          ];
                        }

                        if (keyNameQuery) {
                          const nameOpts = await this.searchExtensions(
                            keyNameQuery
                          );
                          this.keyNameOpts[`${keyPrefix}NameOpts`] = [
                            ...nameOpts
                          ];
                        }
                      }
                    }
                  }
                }
              }
            }
          }

          for (let i = 2; i <= maxGroupIdx; i++) {
            this.keySettingGroups.push(i);
          }
        } else {
          this.keySettingGroups = [2];
        }
      }
    },

    selectedDevice(val) {
      if (val) {
        const {
          tenantId,
          name,
          mac,
          phPmId,
          phonebooks,
          phoneLines,
          buttonlayouts,
          sidecarButtonLayouts
        } = val;
        this.editForm.tenantId = tenantId;
        this.editForm.name = name;
        this.editForm.mac = mac;
        this.editForm.phPmId = phPmId;
        this.editForm.buttonlayouts = [...buttonlayouts];
        this.editForm.sidecarButtonLayouts = [...sidecarButtonLayouts];

        const phonemodel = this.deviceModelOptions.find(p => p.id === phPmId);
        const { endline } = phonemodel;
        this.numOfPhoneLinesAllowed = endline;

        if (phonebooks.length) {
          this.editForm.phonebooks = phonebooks.map(p => p.phonebookId);
        }

        this.phoneLines = [...phoneLines];
      }
    }
  },

  methods: {
    removeKeySettingGroup(groupIdx) {
      const groupNumber = this.keySettingGroups[groupIdx];

      this.keySettings[`group${groupNumber}`].forEach(provision => {
        if (!/Key\s\d{1,2}\sType/.test(provision.fieldName)) {
          provision.value = provision.defaultValue
            ? provision.defaultValue
            : null;
        } else {
          provision.value = null;
        }
      });

      this.keyNameOpts[`Key${groupNumber}NameOpts`] = [];
      this.keyNumberOpts[`Key${groupNumber}NumberOpts`] = [];

      this.keySettingGroups.splice(groupIdx, 1);
      if (groupIdx === 0) {
        this.keySettingGroups = [2];
      }
    },

    addKeySettingGroup() {
      const lastGroupIdx = this.keySettingGroups[
        this.keySettingGroups.length - 1
      ];
      this.keySettingGroups.push(lastGroupIdx + 1);
    },

    fieldIsAllowed(groupIdx) {
      return (
        this.keySettings[`group${groupIdx}`].findIndex(p =>
          /Key\s\d{1,2}\sType/.test(p.fieldName)
        ) === -1
      );
    },

    onKeyTypeSelection(groupIdx, provisionIdx, fieldName, provision, keyType) {
      // 16 BLF
      // 10 Park
      // 13 SpeedDial
      this.keyTypes[fieldName.replace(/\s/g, "")] = keyType;
      provision.value = keyType;

      const keyPrefix = this.getKeyPrefix(fieldName);
      if (keyType === "10" || keyType === "19") {
        // If keyType is "Park"
/*        this.keySettings[`group${groupIdx}`][
          provisionIdx + 2
        ].value = this.parkingLotName;*/
        this.keyNumberOpts[`${keyPrefix}NumberOpts`] = [
          ...this.parkingLotOptions
        ];
      } else if (keyType === "16" || keyType === "11") {
        // If keyType is "BLF"
        this.keySettings[`group${groupIdx}`][provisionIdx + 2].value = "";
        this.keyNumberOpts[`${keyPrefix}NumberOpts`] = [];
      } else {
        // If keyType is "SpeedDial"
        this.keySettings[`group${groupIdx}`][provisionIdx + 2].value = "";
      }
      this.keySettings[`group${groupIdx}`][provisionIdx + 1].value = "";
    },

    clearFields() {
      this.keySettings = null;
      this.keySettingGroups = [];

      this.numOfPhoneLinesAllowed = null;
      this.activeTabName = "general";
      this.keyTypes = {};

      this.keyNameOpts = {
        Key2NameOpts: [],
        Key3NameOpts: [],
        Key4NameOpts: [],
        Key5NameOpts: [],
        Key6NameOpts: [],
        Key7NameOpts: [],
        Key8NameOpts: [],
        Key9NameOpts: [],
        Key10NameOpts: [],
        Key11NameOpts: [],
        Key12NameOpts: [],
        Key13NameOpts: [],
        Key14NameOpts: [],
        Key15NameOpts: [],
        Key16NameOpts: [],
        Key17NameOpts: [],
        Key18NameOpts: [],
        Key19NameOpts: [],
        Key20NameOpts: [],
        Key21NameOpts: [],
        Key22NameOpts: [],
        Key23NameOpts: [],
        Key24NameOpts: [],
        Key25NameOpts: [],
        Key26NameOpts: [],
        Key27NameOpts: [],
      };

      this.keyNumberOpts = {
        Key2NumberOpts: [],
        Key3NumberOpts: [],
        Key4NumberOpts: [],
        Key5NumberOpts: [],
        Key6NumberOpts: [],
        Key7NumberOpts: [],
        Key8NumberOpts: [],
        Key9NumberOpts: [],
        Key10NumberOpts: [],
        Key11NumberOpts: [],
        Key12NumberOpts: [],
        Key13NumberOpts: [],
        Key14NumberOpts: [],
        Key15NumberOpts: [],
        Key16NumberOpts: [],
        Key17NumberOpts: [],
        Key18NumberOpts: [],
        Key19NumberOpts: [],
        Key20NumberOpts: [],
        Key21NameOpts: [],
        Key22NameOpts: [],
        Key23NameOpts: [],
        Key24NameOpts: [],
        Key25NameOpts: [],
        Key26NameOpts: [],
        Key27NameOpts: [],
      };

      this.phoneLines = [];

      this.editForm = {
        tenantId: null,
        name: "",
        mac: "",
        phPmId: null,
        phoneLines: [],
        phonebooks: [],
        settings: [],
        buttonlayouts: [],
        sidecarButtonLayouts: []
      };

      this.provisionFields = [];
    },

    async editOperation() {
      const editBody = { ...this.editForm };

      // ⭐️ If `Polycom` is Selected ⭐️ //
      if (this.phPmId === 22) {
        if (this.phoneLines.length === 1 && !this.phoneLines[0].value) {
          editBody.phoneLines = [];
        } else {
          editBody.phoneLines = this.phoneLines.map(p => {
            return {
              exId: p.value ? p.value : 0,
              configVar: p.configVar
            };
          });
        }
      } else {
        if (this.phoneLines.length === 1 && !this.phoneLines[0].value) {
          editBody.phoneLines = [];
        } else {
          editBody.phoneLines = this.phoneLines.map(p => {
            return p.value ? p.value : 0;
          });
        }
      }

      this.provisionFields.forEach(provision => {
        if (provision.value !== null && provision.value !== "") {
          const alId =
            typeof provision.labelId === "number" ? provision.labelId : 0;
          editBody.settings.push({
            tenantId: this.selectedTenant.id,
            type: provision.labelType,
            alId,
            order: "0",
            avValue: provision.value
          });
        }
      });

      if (this.keySettings) {
        this.keySettingGroups.forEach(group => {
          this.keySettings[`group${group}`].forEach(provision => {
            if (provision.value !== null && provision.value !== "") {
              editBody.settings.push({
                tenantId: this.selectedTenant.id,
                type: provision.labelType,
                alId: provision.labelId,
                order: "0",
                avValue: provision.value
              });
            }
          });
        });
      }

      //check to prompt user for device reboot
      const hasLines = !!editBody.phoneLines.length;
      editBody.rebootDevice = false;

      if (hasLines) {
        await this.$confirm("Reboot device after saving?", {
          confirmButtonText: "Yes",
          cancelButtonText: "No"
        })
          .then(() => {
            editBody.rebootDevice = true;
            for (let i = 0; i < editBody.phoneLines.length; i++) {
              const item = editBody.phoneLines[i];
              if (typeof item === "object") {
                if (item.exId !== 0) {
                  editBody.rebootExtId = item.exId;
                  break;
                }
              } else {
                if (item !== 0) {
                  editBody.rebootExtId = item;
                  break;
                }
              }
            }
          })
          .catch(() => {
            editBody.rebootDevice = false;
          });
      }

      this.$emit("editOperation", editBody);
    },

    cancelOperation() {
      this.$emit("cancelOperation", "edit");
    },

    async searchExtensions(query, sortby = "name") {
      const searchTerm = (query + "")
        .replace(/[\\"']/g, "\\$&")
        .replace(/\u0000/g, "\\0");

      let queryTerm = {
        tenantId: this.selectedTenant.id,
        $limit: 25,
        $or: [
          { number: { $like: `%${searchTerm}%` } },
          { name: { $like: `%${searchTerm}%` } }
        ]
      };

      if (sortby === "name") {
        queryTerm.$sort = { name: 1 };
      } else if (sortby === "number") {
        queryTerm.$sort = { number: 1 };
      }

      const exts = await feathersClient.service("extensions").find({
        query: queryTerm
      });

      return exts;
    },

    async remoteSearchExtensionsName(query, fieldName) {
      if (query && query.length) {
        const exts = await this.searchExtensions(query);
        this.keyNameOpts[`${this.getKeyPrefix(fieldName)}NameOpts`] = [...exts];
      }
    },

    async remoteSearchExtensionsNumber(query, fieldName) {
      if (query && query.length) {
        const sortby = "number";
        const exts = await this.searchExtensions(query, sortby);
        this.keyNumberOpts[`${this.getKeyPrefix(fieldName)}NumberOpts`] = [
          ...exts
        ];
      }
    },

    async remoteSearchPhoneLines(query, idx) {
      if (query && query.length) {
        const exts = await this.searchExtensions(query);
        this.phoneLines[idx].options = [...exts];
        this.phoneLines = [...this.phoneLines];
      }
    },

    getParkingLotOptions() {
      this.parkingLotOptions = [];
      feathersClient
        .service("parkinglots")
        .find({
          query: {
            tenantId: this.selectedTenant.id
          }
        })
        .then(res => {
          const { name, start, end } = res[0];

          this.parkingLotName = name;
          for (let i = start; i <= end; i++) {
            this.parkingLotOptions.push({
              label: `${i}`, //DO NOT CHANGE THIS!!!!!
              value: `${i}`
            });
          }
        });
    },

    getKeyPrefix(fieldName) {
      if (/Key\s\d{2}/.test(fieldName)) {
        return fieldName.replace(/\s/g, "").substr(0, 5);
      }
      return fieldName.replace(/\s/g, "").substr(0, 4);
    },

    addPhoneLine() {
      // ⭐️ If 'Polycom' is selected ⭐️ //
      if (this.phPmId === 22) {
        this.phoneLines.push({
          options: [],
          value: null,
          configVar: 1
        });
        return;
      }
      this.phoneLines.push({
        options: [],
        value: null
      });
    },

    removePhoneLine(idx) {
      this.phoneLines.splice(idx, 1);
    }
  },

  created() {
    this.getParkingLotOptions();
  }
};
</script>

<style lang="scss" scoped>
.el-tabs {
  max-width: 600px;
  margin: auto;
}

.row-button {
  height: 49px;
  width: 49px;
}

.group-container {
  position: relative;

  .group-delete-btn {
    position: absolute;
    right: 0;
    top: 60%;
    transform: translateY(-50%);
  }
}
</style>
